<template>
  <div class="navbar-wrapper" :class="{ 'dark-mode': isDarkMode }">
    <!-- Top Bar -->
    <div class="top-bar">
      <div class="container top-bar__row">
        <div class="contact-info">
          <span class="contact-item">
            <i class="fas fa-map-marker-alt"></i> 12 Bd Hassan Al Alaoui Rés. Nissrine – Ain Borja, Casablanca
          </span>
          <span class="contact-item">
            <i class="fas fa-phone"></i> +212 522 600 506 / +212 522 620 507
          </span>
          <span class="contact-item">
            <i class="fas fa-envelope"></i> info@supracare.ma
          </span>
        </div>
        <div class="top-bar-right">
          <span class="hours"><i class="fas fa-clock"></i> Lun-Ven 08h30 - 18h30 Sam 08h30 - 13h00</span>
        <div class="social-icons">
        <a href="https://facebook.com" target="_blank"><i class="fab fa-facebook-f"></i></a>
        <a href="https://twitter.com" target="_blank"><i class="fab fa-twitter"></i></a>
        <a href="https://linkedin.com" target="_blank"><i class="fab fa-linkedin-in"></i></a>
        <a href="https://instagram.com" target="_blank"><i class="fab fa-instagram"></i></a>
      </div>
        </div>
      </div>
    </div>

    <!-- Header Middle -->
    <div class="header-middle">
      <div class="container header-middle__row">
        <div class="logo-container">
          <router-link to="/">
            <img src="@/assets/logo.png" alt="Supracare" class="logo-image" />
          </router-link>
        </div>

        <div class="search-container">
          <input
            type="search"
            v-model="navbarSearchQuery"
            placeholder="Rechercher un produit..."
            class="search-input"
            @keyup.enter="searchProducts"
          />
          <button class="search-btn" @click="searchProducts" aria-label="Rechercher">
            <i class="fas fa-search"></i>
          </button>
        </div>

        <div class="user-actions">
          <!-- ===== PANIER AVEC POPUP ===== -->
          <div 
            class="cart-wrapper"
            ref="cartRef"
            @mouseenter="showCartPopup = true"
            @mouseleave="showCartPopup = false"
          >
            <div class="action-link cart-link" @click="goToCart">
              <div class="action-icon-wrapper">
                <i class="fas fa-shopping-cart"></i>
                <span v-if="cartCount > 0" class="cart-badge">{{ cartCount }}</span>
              </div>
              <span class="action-label">Panier</span>
            </div>
          </div>

          <!-- POPUP PANIER - AVEC STYLE FORT -->
          <teleport to="body">
            <div
              v-if="showCartPopup && cart.length > 0"
              class="cart-popup-teleport"
              :style="popupPosition"
              @mouseenter="showCartPopup = true"
              @mouseleave="showCartPopup = false"
            >
              <div class="cart-summary-header">
                <h3>Mon panier ({{ cart.length }} article<span v-if="cart.length > 1">s</span>)</h3>
              </div>

              <div class="cart-items">
                <div v-for="(item, index) in cart" :key="index" class="cart-item">
                  <img 
                    :src="getProductImage(item.image)" 
                    :alt="item.name" 
                    class="product-image" 
                    @error="handleImageError"
                  />
                  <div class="item-details">
                    <h4 class="item-name">{{ item.name }}</h4>
                    <p class="item-quantity">Qté : {{ item.quantity }}</p>
                    <p class="item-price">{{ (item.price * item.quantity).toFixed(2) }} MAD</p>
                  </div>
                </div>
              </div>

              <div class="cart-total">
                <p>Total TTC : <span>{{ totalPrice.toFixed(2) }} MAD</span></p>
              </div>

              <div class="cart-actions">
                <button @click="goToCart" class="btn-view-cart">Voir panier</button>
                <button @click="goToShipping" class="btn-checkout">Commander</button>
              </div>
            </div>
          </teleport>

          <!-- Wishlist -->
          <router-link to="/wishlist" class="action-link">
            <div class="action-icon-wrapper">
              <i class="fas fa-heart"></i>
              <span v-if="wishlistCount > 0" class="action-badge heart">{{ wishlistCount }}</span>
            </div>
            <span class="action-label">Favoris</span>
          </router-link>

          <!-- Client connecté -->
          <div v-if="isLoggedIn && userRole === 'account'" class="user-dropdown">
            <button @click="toggleDropdown" class="action-link user-btn">
              <div class="action-icon-wrapper">
                <i class="fas fa-user-circle"></i>
              </div>
              <span class="action-label">{{ userName }}</span>
              <i class="fas fa-chevron-down dropdown-arrow" :class="{ rotated: showDropdown }"></i>
            </button>
            <transition name="dropdown">
              <ul v-show="showDropdown" class="dropdown-menu">
                <li @click="closeDropdownAndNavigate('/account')">
                  <i class="fas fa-user"></i> Mon compte
                </li>
                <li @click="closeDropdownAndNavigate('/wishlist')">
                  <i class="fas fa-heart"></i> Mes favoris
                </li>
                <li @click="closeDropdownAndNavigate('/orderStatus')">
                  <i class="fas fa-box"></i> Mes commandes
                </li>
                <li class="divider"></li>
                <li @click="logout">
                  <i class="fas fa-sign-out-alt"></i> Déconnexion
                </li>
              </ul>
            </transition>
          </div>

          <!-- Admin connecté -->
          <div v-else-if="isLoggedIn && userRole === 'admin'" class="user-dropdown">
            <button @click="toggleDropdown" class="action-link user-btn">
              <div class="action-icon-wrapper">
                <i class="fas fa-user-shield"></i>
              </div>
              <span class="action-label">Admin</span>
              <i class="fas fa-chevron-down dropdown-arrow" :class="{ rotated: showDropdown }"></i>
            </button>
            <transition name="dropdown">
              <ul v-show="showDropdown" class="dropdown-menu">
                <li @click="closeDropdownAndNavigate('/dashboardAdmin')">
                  <i class="fas fa-chart-line"></i> Tableau de bord
                </li>
                <li @click="closeDropdownAndNavigate('/adminOrders')">
                  <i class="fas fa-box"></i> Commandes
                </li>
                <li @click="closeDropdownAndNavigate('/products')">
                  <i class="fas fa-flask"></i> Produits
                </li>
                <li @click="closeDropdownAndNavigate('/clients')">
                  <i class="fas fa-users"></i> Clients
                </li>
                <li class="divider"></li>
                <li @click="logout">
                  <i class="fas fa-sign-out-alt"></i> Déconnexion
                </li>
              </ul>
            </transition>
          </div>

          <!-- Non connecté -->
          <router-link v-else to="/connexion" class="action-link login-link">
            <div class="action-icon-wrapper">
              <i class="fas fa-user"></i>
            </div>
            <span class="action-label">Connexion</span>
          </router-link>
        </div>

        <router-link to="/shop" class="shop-button">
          <span>Shop en Ligne</span>
          <i class="fas fa-arrow-right"></i>
        </router-link>
      </div>
    </div>

    <!-- Navigation -->
    <nav class="navbar" :class="{ 'navbar-scrolled': isScrolled }">
      <div class="container navbar__row">
        <button class="menu-toggle" @click="toggleSidebar" aria-label="Menu">
          <i :class="isSidebarOpen ? 'fas fa-times' : 'fas fa-bars'"></i>
        </button>

        <ul class="nav-links" :class="{ 'nav-active': isNavOpen }">
          <li class="nav-item">
            <DropdownComponent @navigateToProducts="navigateToProducts" />
          </li>
          <li class="nav-item" @click="closeNav">
            <a href="http://www.supracare.ma" target="_blank">Présentation</a>
          </li>
          <li class="nav-item" @click="closeNav">
            <a href="http://www.supracare.ma/services" target="_blank">Garantie</a>
          </li>
          <li class="nav-item" @click="closeNav">
            <router-link to="/marques">Marques</router-link>
          </li>
          <li class="nav-item" @click="closeNav">
            <a href="http://www.supracare.ma/contact" target="_blank">Contact</a>
          </li>
        </ul>
      </div>
    </nav>

    <!-- Sidebar -->
    <Sidebar :isOpen="isSidebarOpen" @closeSidebar="toggleSidebar" />

    <!-- WhatsApp -->
    <transition name="fade">
      <a
        v-if="!isAdminPage"
        href="https://wa.me/+212662297001"
        target="_blank"
        class="whatsapp-float"
        aria-label="WhatsApp"
      >
        <i class="fab fa-whatsapp"></i>
      </a>
    </transition>
  </div>
</template>

<script>
import { store } from "@/Store";
import { wishlistStore } from "@/wishlisteStore.js";
import DropdownComponent from "@/components/DropdownComponent.vue";
import Sidebar from "@/components/Sidebar.vue";
import axios from "axios";

export default {
  name: "NavbarI",
  components: { DropdownComponent, Sidebar },
  data() {
    return {
      isSidebarOpen: false,
      isNavOpen: false,
      isLoggedIn: false,
      userName: "",
      showDropdown: false,
      userRole: "",
      navbarSearchQuery: "",
      products: [],
      isScrolled: false,
      isDarkMode: false,
      showCartPopup: false,
      popupTop: 0,
      popupRight: 0,
    };
  },
  computed: {
    cartCount() {
      return store.cartCount;
    },
    wishlistCount() {
      return wishlistStore.count;
    },
    cart() {
      return store.cart;
    },
    totalPrice() {
      return this.cart.reduce((total, item) => total + item.price * item.quantity, 0);
    },
    popupPosition() {
      return {
        position: "fixed",
        top: `${this.popupTop}px`,
        right: `${this.popupRight}px`,
        zIndex: 99999,
      };
    },
    isAdminPage() {
      return this.$route.path.includes("dashboardAdmin") ||
             this.$route.path.includes("adminOrders") ||
             this.$route.path.includes("products") ||
             this.$route.path.includes("clients") ||
             this.$route.path.includes("settings") ||
             this.$route.path.includes("notifications");
    },
  },
  mounted() {
    this.loadUserData();
    this.fetchWishlistCount();
    this.checkDarkMode();
    window.addEventListener("scroll", this.handleScroll);
    window.addEventListener("user-updated", this.loadUserData);
    window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", this.checkDarkMode);
  },
  beforeUnmount() {
    window.removeEventListener("scroll", this.handleScroll);
    window.removeEventListener("user-updated", this.loadUserData);
    window.matchMedia("(prefers-color-scheme: dark)").removeEventListener("change", this.checkDarkMode);
  },
  watch: {
    $route() {
      this.loadUserData();
      this.fetchWishlistCount();
      this.closeNav();
      this.showDropdown = false;
      this.showCartPopup = false;
    },
    showCartPopup(val) {
      if (val) {
        this.updatePopupPosition();
      }
    },
  },
  methods: {
    getProductImage(imagePath) {
      if (!imagePath) return 'http://localhost:2028/uploads/default-product.jpg';
      if (imagePath.startsWith('http')) return imagePath;
      if (imagePath.startsWith('/uploads/')) return `http://localhost:2028${imagePath}`;
      return `http://localhost:2028/uploads/${imagePath}`;
    },
    handleImageError(e) {
      e.target.src = 'http://localhost:2028/uploads/default-product.jpg';
    },
    goToCart() {
      this.showCartPopup = false;
      this.$router.push("/panier");
    },
    goToShipping() {
      this.showCartPopup = false;
      this.$router.push("/shipping");
    },
    updatePopupPosition() {
      this.$nextTick(() => {
        const cartIcon = this.$el.querySelector(".cart-wrapper");
        if (cartIcon) {
          const rect = cartIcon.getBoundingClientRect();
          this.popupTop = rect.bottom + 10;
          this.popupRight = window.innerWidth - rect.right;
        }
      });
    },
    checkDarkMode() {
      this.isDarkMode = window.matchMedia("(prefers-color-scheme: dark)").matches;
    },
    toggleSidebar() {
      this.isSidebarOpen = !this.isSidebarOpen;
      document.body.style.overflow = this.isSidebarOpen ? 'hidden' : '';
    },
    toggleNav() {
      this.isNavOpen = !this.isNavOpen;
    },
    closeNav() {
      this.isNavOpen = false;
    },
    navigateToProducts() {
      this.$router.push("/produits");
    },
    toggleDropdown() {
      this.showDropdown = !this.showDropdown;
    },
    closeDropdownAndNavigate(route) {
      this.showDropdown = false;
      this.$router.push(route);
    },
    handleScroll() {
      this.isScrolled = window.scrollY > 50;
    },
    loadUserData() {
      const userData = sessionStorage.getItem("user");
      if (userData) {
        const user = JSON.parse(userData);
        this.isLoggedIn = true;
        this.userRole = user.role || "account";
        this.userName = `${user.prenom || ""} ${user.nom || ""}`.trim();
      } else {
        this.isLoggedIn = false;
        this.userRole = "";
        this.userName = "";
      }
    },
    logout() {
      sessionStorage.clear();
      this.isLoggedIn = false;
      this.userName = "";
      wishlistStore.setCount(0);
      this.showDropdown = false;
      this.$router.push("/connexion");
    },
    async searchProducts() {
      if (!this.navbarSearchQuery.trim()) return;
      try {
        const res = await axios.get(
          `http://localhost:2028/api/products?search=${this.navbarSearchQuery}`
        );
        this.products = res.data;
        if (this.products.length > 0) {
          this.$router.push({ 
            path: "/shop", 
            query: { search: this.navbarSearchQuery } 
          });
        }
      } catch (error) {
        console.error("Erreur recherche:", error);
      }
    },
    async fetchWishlistCount() {
      try {
        const token = sessionStorage.getItem("token");
        if (!token) {
          wishlistStore.setCount(0);
          return;
        }
        const response = await axios.get("http://localhost:2028/api/wishlist", {
          headers: { Authorization: `Bearer ${token}` },
        });
        wishlistStore.setCount(response.data.length);
      } catch (error) {
        console.error("Erreur wishlist:", error);
      }
    },
  },
};
</script>

<style scoped>
/* ============================================
   VARIABLES
   ============================================ */
:root {
  --brand: #00998B;
  --brand-dark: #05305b;
  --brand-light: #00bfa5;
  --accent: #e0240b;
  --light-bg: #f7f9fc;
  --border: #edf2f7;
  --shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  --radius: 12px;
  --transition: 0.3s cubic-bezier(0.25, 1, 0.5, 1);
  --topbar-bg: #0a2a4a;
  --topbar-text: #ffffff;
  --surface: #ffffff;
  --surface-hover: #f7f9fc;
  --text-primary: #1a1a2e;
  --text-secondary: #6c7a89;
  --input-bg: #f7f9fc;
  --dropdown-bg: #ffffff;
  --dropdown-hover: #f7f9fc;
  --border-color: #edf2f7;
  --cart-bg: #ffffff;
  --cart-border: #e0e0e0;
  --cart-text: #1a1a2e;
  --cart-text-secondary: #6c7a89;
  --cart-total-bg: #f7f9fc;
  --cart-item-border: #edf2f7;
  --cart-btn-secondary: #e0240b;
  --cart-btn-secondary-hover: #c01f0a;
}

.container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
}

/* --- Top Bar --- */
.top-bar {
  background: #0a2a4a;
  color: #ffffff;
  font-size: 12px;
  padding: 8px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  display: block !important;
}

.top-bar__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.contact-info {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 6px;
  opacity: 0.85;
  transition: opacity 0.3s ease;
}

.contact-item:hover {
  opacity: 1;
}

.contact-item i {
  font-size: 13px;
  color: #00bfa5;
}

.top-bar-right {
  display: flex;
  align-items: center;
  gap: 20px;
}

.hours {
  display: flex;
  align-items: center;
  gap: 6px;
  opacity: 0.85;
}

.hours i {
  color: #00bfa5;
}

.social-icons {
  display: flex;
  gap: 12px;
}

.social-icons a {
  color: rgba(255, 255, 255, 0.7);
  transition: all 0.3s ease;
  font-size: 14px;
}

.social-icons a:hover {
  color: white;
  transform: translateY(-2px);
}

/* --- Header Middle --- */
.header-middle {
  background: var(--surface);
  padding: 12px 0;
  border-bottom: 1px solid var(--border);
  box-shadow: var(--shadow);
  position: sticky;
  top: 0;
  z-index: 999;
}

.header-middle__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

.logo-container {
  flex-shrink: 0;
}

.logo-image {
  height: 60px;
  width: auto;
  transition: transform 0.3s ease;
}

.logo-image:hover {
  transform: scale(1.03);
}

/* --- Search --- */
.search-container {
  flex: 1;
  position: relative;
  max-width: 500px;
}

.search-input {
  width: 100%;
  padding: 10px 48px 10px 16px;
  border: 2px solid var(--border-color);
  border-radius: var(--radius);
  font-size: 14px;
  transition: all 0.3s ease;
  background: var(--input-bg);
  color: var(--text-primary);
}

.search-input:focus {
  outline: none;
  border-color: var(--brand);
  background: var(--surface);
  box-shadow: 0 0 0 4px rgba(0, 153, 139, 0.1);
}

.search-btn {
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
  background: var(--brand);
  color: white;
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.search-btn:hover {
  background: var(--brand-dark);
  transform: translateY(-50%) scale(1.05);
}

/* --- User Actions --- */
.user-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.action-link {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-decoration: none;
  color: var(--text-primary);
  font-size: 11px;
  font-weight: 500;
  padding: 4px 10px;
  border-radius: 8px;
  transition: all 0.3s ease;
  cursor: pointer;
  background: none;
  border: none;
  position: relative;
}

.action-link:hover {
  color: var(--brand);
  background: rgba(0, 153, 139, 0.05);
}

.action-icon-wrapper {
  position: relative;
  font-size: 20px;
}

.action-icon-wrapper i {
  transition: color 0.3s ease;
}

.action-link:hover .action-icon-wrapper i {
  color: var(--brand);
}

/* ✅ BADGE ROUGE PANIER */
.cart-badge {
  position: absolute;
  top: -8px;
  right: -10px;
  background: #e0240b !important;
  color: white !important;
  font-size: 10px;
  font-weight: 700;
  min-width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
  box-shadow: 0 2px 6px rgba(224, 36, 11, 0.4);
  border: 2px solid var(--surface);
  line-height: 1;
  z-index: 10;
}

/* ✅ Autres badges */
.action-badge {
  position: absolute;
  top: -6px;
  right: -8px;
  background: #e0245e;
  color: white;
  font-size: 9px;
  font-weight: 700;
  min-width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  border: 2px solid var(--surface);
}

.action-badge.heart {
  background: #e0245e;
}

.action-label {
  font-size: 10px;
  margin-top: 2px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

/* --- Panier --- */
.cart-wrapper {
  cursor: pointer;
}

.cart-link {
  position: relative;
}

/* ============================================
   POPUP PANIER - 100% OPAQUE
   ============================================ */
.cart-popup-teleport {
  width: 340px;
  max-height: 460px;
  background: #ffffff !important;
  background-color: #ffffff !important;
  border: 1px solid #e0e0e0 !important;
  border-radius: 12px !important;
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.2) !important;
  padding: 18px 20px !important;
  z-index: 99999 !important;
  overflow: hidden !important;
  display: flex !important;
  flex-direction: column !important;
  animation: cartSlideIn 0.25s ease !important;
  /* ✅ SUPPRIME TOUTE TRANSPARENCE */
  opacity: 1 !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  /* ✅ FORCE L'OPACITÉ */
  pointer-events: auto !important;
}

@keyframes cartSlideIn {
  from {
    opacity: 0;
    transform: translateY(-10px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.cart-summary-header h3 {
  margin: 0 0 14px !important;
  font-size: 16px !important;
  font-weight: 700 !important;
  color: #1a1a2e !important;
  border-bottom: 2px solid #edf2f7 !important;
  padding-bottom: 10px !important;
}

.cart-items {
  flex: 1;
  overflow-y: auto;
  max-height: 280px;
  margin-bottom: 14px;
}

.cart-items::-webkit-scrollbar {
  width: 4px;
}

.cart-items::-webkit-scrollbar-track {
  background: #f7f9fc;
  border-radius: 4px;
}

.cart-items::-webkit-scrollbar-thumb {
  background: #00998B;
  border-radius: 4px;
}

.cart-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #edf2f7;
}

.cart-item:last-child {
  border-bottom: none;
}

.cart-item .product-image {
  width: 52px;
  height: 52px;
  object-fit: cover;
  border-radius: 8px;
  flex-shrink: 0;
  border: 1px solid #edf2f7;
  background: #f7f9fc;
}

.cart-item .item-details {
  flex: 1;
  min-width: 0;
}

.cart-item .item-name {
  font-size: 13px;
  font-weight: 600;
  margin: 0 0 2px;
  color: #1a1a2e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cart-item .item-quantity {
  font-size: 12px;
  color: #6c7a89;
  margin: 0;
}

.cart-item .item-price {
  font-size: 13px;
  font-weight: 700;
  color: #00998B;
  margin: 0;
}

.cart-total {
  padding: 12px 0 4px;
  border-top: 2px solid #edf2f7;
  text-align: right;
  background: #f7f9fc;
  margin: 0 -20px;
  padding: 12px 20px 8px;
}

.cart-total p {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
}

.cart-total span {
  color: #00998B;
  font-weight: 700;
  font-size: 18px;
}

.cart-actions {
  display: flex;
  gap: 10px;
  margin-top: 10px;
}

.cart-actions button {
  flex: 1;
  padding: 10px 16px;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-view-cart {
  background: #f7f9fc;
  color: #1a1a2e;
  border: 1px solid #edf2f7;
}

.btn-view-cart:hover {
  background: #edf2f7;
  transform: translateY(-2px);
}

.btn-checkout {
  background: #e0240b;
  color: white;
}

.btn-checkout:hover {
  background: #c01f0a;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(224, 36, 11, 0.3);
}

/* --- User Dropdown --- */
.user-dropdown {
  position: relative;
}

.user-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  background: none;
  border: none;
  cursor: pointer;
}

.dropdown-arrow {
  font-size: 10px;
  transition: transform 0.3s ease;
}

.dropdown-arrow.rotated {
  transform: rotate(180deg);
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 220px;
  background: #ffffff;
  border-radius: var(--radius);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
  padding: 8px;
  list-style: none;
  border: 1px solid var(--border-color);
  z-index: 100;
}

.dropdown-menu li {
  padding: 0;
  margin: 0;
}

.dropdown-menu li a,
.dropdown-menu li {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  border-radius: 8px;
  color: var(--text-primary);
  text-decoration: none;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s ease;
  cursor: pointer;
}

.dropdown-menu li a:hover,
.dropdown-menu li:hover {
  background: var(--dropdown-hover);
  color: var(--brand);
}

.dropdown-menu li a i,
.dropdown-menu li i {
  width: 18px;
  text-align: center;
  font-size: 14px;
  color: var(--text-secondary);
}

.dropdown-menu .divider {
  height: 1px;
  background: var(--border-color);
  margin: 6px 8px;
  padding: 0 !important;
  cursor: default !important;
}

.dropdown-menu .divider:hover {
  background: var(--border-color) !important;
}

/* --- Shop Button --- */
.shop-button {
  display: flex !important;
  align-items: center;
  gap: 8px;
  padding: 10px 24px;
  background: #e0240b;
  color: white !important;
  border-radius: 30px;
  font-size: 14px;
  font-weight: 700;
  text-decoration: none;
  transition: all 0.3s ease;
  white-space: nowrap;
  flex-shrink: 0;
  border: none;
  box-shadow: 0 4px 12px rgba(224, 36, 11, 0.25);
}

.shop-button:hover {
  transform: scale(1.05);
  background: #c01f0a;
  box-shadow: 0 6px 24px rgba(224, 36, 11, 0.4);
  color: white !important;
}

.shop-button i {
  font-size: 12px;
  transition: transform 0.3s ease;
}

.shop-button:hover i {
  transform: translateX(4px);
}

/* --- Navbar --- */
.navbar {
  background: var(--surface);
  border-bottom: 1px solid var(--border-color);
  transition: all 0.3s ease;
}

.navbar-scrolled {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.navbar__row {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 0;
  gap: 16px;
}

.menu-toggle {
  display: none;
  background: none;
  border: none;
  font-size: 22px;
  color: var(--text-primary);
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: background 0.3s ease;
}

.menu-toggle:hover {
  background: var(--light-bg);
}

/* --- Nav Links --- */
.nav-links {
  display: flex;
  align-items: center;
  gap: 32px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.nav-item {
  position: relative;
}

.nav-item a {
  color: var(--text-primary);
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  padding: 8px 0;
  transition: color 0.3s ease;
  position: relative;
}

.nav-item a::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 0;
  height: 2px;
  background: var(--brand);
  transition: width 0.3s ease;
}

.nav-item a:hover {
  color: var(--brand);
}

.nav-item a:hover::after {
  width: 100%;
}

.nav-item a.router-link-exact-active {
  color: var(--brand);
}

.nav-item a.router-link-exact-active::after {
  width: 100%;
}

/* --- WhatsApp Float --- */
.whatsapp-float {
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 56px;
  height: 56px;
  background: #25D366;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  box-shadow: 0 6px 24px rgba(37, 211, 102, 0.35);
  transition: all 0.3s ease;
  z-index: 100;
  text-decoration: none;
}

.whatsapp-float:hover {
  transform: scale(1.1) translateY(-4px);
  box-shadow: 0 8px 32px rgba(37, 211, 102, 0.45);
}

/* --- Transitions --- */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.4s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.25s ease;
}
.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* ============================================
   MODE SOMBRE
   ============================================ */
.dark-mode {
  --topbar-bg: #061a2e;
  --topbar-text: #dfe8ff;
  --surface: #0f1720;
  --surface-hover: #1a2532;
  --text-primary: #e6edf1;
  --text-secondary: #8b97a3;
  --input-bg: #1a2532;
  --dropdown-bg: #1a2532;
  --dropdown-hover: #0f1720;
  --border-color: #2a3645;
  --cart-bg: #1a2532;
  --cart-border: #2a3645;
  --cart-text: #e6edf1;
  --cart-text-secondary: #8b97a3;
  --cart-total-bg: #0f1720;
  --cart-item-border: #2a3645;
  --cart-btn-secondary: #c62828;
  --cart-btn-secondary-hover: #ef5350;
}

.dark-mode .top-bar {
  background: var(--topbar-bg);
  color: var(--topbar-text);
  border-bottom-color: rgba(255, 255, 255, 0.06);
}

.dark-mode .header-middle {
  background: var(--surface);
  border-bottom-color: var(--border-color);
  box-shadow: var(--shadow);
}

.dark-mode .search-input {
  background: var(--input-bg);
  border-color: var(--border-color);
  color: var(--text-primary);
}

.dark-mode .search-input:focus {
  border-color: var(--brand-light);
  background: var(--surface);
  box-shadow: 0 0 0 4px rgba(0, 191, 165, 0.15);
}

.dark-mode .action-link {
  color: var(--text-secondary);
}

.dark-mode .action-link:hover {
  color: var(--brand-light);
  background: rgba(0, 153, 139, 0.1);
}

.dark-mode .action-link:hover .action-icon-wrapper i {
  color: var(--brand-light);
}

.dark-mode .cart-badge {
  border-color: var(--surface);
  background: #e53935 !important;
}

.dark-mode .action-badge {
  border-color: var(--surface);
}

.dark-mode .dropdown-menu {
  background: #1a2532;
  border-color: #2a3645;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
}

.dark-mode .dropdown-menu li a,
.dark-mode .dropdown-menu li {
  color: #8b97a3;
}

.dark-mode .dropdown-menu li a:hover,
.dark-mode .dropdown-menu li:hover {
  background: #0f1720;
  color: #4ade80;
}

.dark-mode .dropdown-menu li a i,
.dark-mode .dropdown-menu li i {
  color: #6c7a89;
}

.dark-mode .dropdown-menu .divider {
  background: #2a3645;
}

.dark-mode .navbar {
  background: var(--surface);
  border-bottom-color: var(--border-color);
}

.dark-mode .nav-item a {
  color: var(--text-secondary);
}

.dark-mode .nav-item a:hover {
  color: var(--brand-light);
}

.dark-mode .nav-item a.router-link-exact-active {
  color: var(--brand-light);
}

.dark-mode .nav-item a::after {
  background: var(--brand-light);
}

.dark-mode .menu-toggle {
  color: var(--text-secondary);
}

.dark-mode .menu-toggle:hover {
  background: var(--input-bg);
}

.dark-mode .shop-button {
  background: #e53935;
  color: white !important;
  box-shadow: 0 4px 16px rgba(229, 57, 53, 0.3);
}

.dark-mode .shop-button:hover {
  background: #c62828;
  box-shadow: 0 6px 24px rgba(229, 57, 53, 0.4);
  color: white !important;
}

.dark-mode .whatsapp-float {
  background: #20b358;
  box-shadow: 0 6px 24px rgba(32, 179, 88, 0.3);
}

.dark-mode .whatsapp-float:hover {
  background: #128c7e;
  box-shadow: 0 8px 32px rgba(32, 179, 88, 0.4);
}

.dark-mode .social-icons a {
  color: rgba(255, 255, 255, 0.5);
}

.dark-mode .social-icons a:hover {
  color: white;
}

/* ============================================
   POPUP PANIER - MODE SOMBRE (100% OPAQUE)
   ============================================ */
.dark-mode .cart-popup-teleport {
  background: #1a2532 !important;
  background-color: #1a2532 !important;
  border: 1px solid #2a3645 !important;
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.6) !important;
  opacity: 1 !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

.dark-mode .cart-summary-header h3 {
  color: #e6edf1 !important;
  border-bottom-color: #2a3645 !important;
}

.dark-mode .cart-item {
  border-bottom-color: #2a3645 !important;
}

.dark-mode .cart-item .item-name {
  color: #e6edf1 !important;
}

.dark-mode .cart-item .item-quantity {
  color: #8b97a3 !important;
}

.dark-mode .cart-item .item-price {
  color: #4ade80 !important;
}

.dark-mode .cart-item .product-image {
  border-color: #2a3645 !important;
  background: #0f1720 !important;
}

.dark-mode .cart-total {
  border-top-color: #2a3645 !important;
  background: #0f1720 !important;
}

.dark-mode .cart-total p {
  color: #e6edf1 !important;
}

.dark-mode .cart-total span {
  color: #4ade80 !important;
}

.dark-mode .btn-view-cart {
  background: #0f1720 !important;
  color: #e6edf1 !important;
  border-color: #2a3645 !important;
}

.dark-mode .btn-view-cart:hover {
  background: #1a2532 !important;
}

.dark-mode .btn-checkout {
  background: #c62828 !important;
  color: white !important;
}

.dark-mode .btn-checkout:hover {
  background: #ef5350 !important;
}

/* --- Responsive --- */
@media (max-width: 1200px) {
  .container { padding: 0 16px; }
  .contact-info { gap: 12px; }
  .nav-links { gap: 20px; }
}

@media (max-width: 1024px) {
  .top-bar { display: none !important; }
  .header-middle__row { flex-wrap: wrap; gap: 12px; }
  .logo-image { height: 48px; }
  .search-container { order: 3; max-width: 100%; flex: 1 1 100%; }
  .user-actions { order: 2; margin-left: auto; }
  .shop-button { display: none !important; }
  .menu-toggle { display: flex; align-items: center; justify-content: center; }
  .navbar__row { justify-content: flex-start; }
  .nav-links { display: none; flex-direction: column; width: 100%; padding: 12px 0; gap: 4px; }
  .nav-active { display: flex; }
  .nav-item { width: 100%; }
  .nav-item a { display: block; padding: 10px 16px; border-radius: 8px; }
  .nav-item a::after { display: none; }
  .nav-item a:hover { background: var(--surface-hover); }
  .dropdown-menu { position: fixed; top: 70px; right: 16px; left: 16px; min-width: auto; }
  .action-label { display: none; }
  .action-link { padding: 4px 8px; }
  .whatsapp-float { width: 48px; height: 48px; font-size: 24px; bottom: 16px; right: 16px; }
  .cart-popup-teleport { width: 300px; max-height: 400px; padding: 14px 16px; }
}

@media (max-width: 768px) {
  .cart-popup-teleport { width: 280px; right: 10px !important; }
}

@media (max-width: 480px) {
  .header-middle__row { gap: 8px; }
  .logo-image { height: 40px; }
  .user-actions { gap: 2px; }
  .action-link { padding: 2px 6px; }
  .action-icon-wrapper { font-size: 18px; }
  .cart-badge { font-size: 8px; min-width: 16px; height: 16px; top: -7px; right: -8px; border-width: 1.5px; }
  .action-badge { font-size: 8px; min-width: 16px; height: 16px; top: -5px; right: -6px; }
  .search-input { font-size: 13px; padding: 8px 40px 8px 12px; }
  .search-btn { width: 32px; height: 32px; font-size: 13px; }
  .dropdown-menu { top: 60px; right: 8px; left: 8px; }
  .whatsapp-float { width: 44px; height: 44px; font-size: 20px; bottom: 12px; right: 12px; }
  .cart-popup-teleport {
    width: 100%;
    max-width: 320px;
    right: 8px !important;
    top: 55px !important;
    border-radius: 8px;
    padding: 12px 14px;
    max-height: 360px;
  }
  .cart-item .product-image { width: 40px; height: 40px; }
  .cart-item .item-name { font-size: 12px; }
  .cart-actions { flex-direction: column; }
  .cart-actions button { padding: 8px 12px; font-size: 12px; }
  .cart-total { padding: 10px 14px 6px; margin: 0 -14px; }
  .cart-total p { font-size: 13px; }
  .cart-total span { font-size: 15px; }
}
</style>